import {
  Box,
  Button,
  ClassNameMap,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import React, { ChangeEvent, Ref, useEffect, useMemo, useState } from 'react';
import { UseFormGetValues, UseFormHandleSubmit } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { Decision } from '~/hooks/useDecisionRegistryData';
import { CustomFieldTypes } from '~/legacy-components/CustomDataFields';
import { LegacyCustomField } from '~/legacy-components/LegacyCustomFields';
import { formatFieldsValue } from '~/legacy-components/LegacyCustomFields/formatFieldsValue';
import {
  AnswerPreProcess,
  CustomField,
} from '~/legacy-components/LegacyCustomFields/types';
import { CustomFieldStyleKeys } from '~/legacy-components/LegacyCustomFields/useStyles';
import { fetchFromOldApi } from '~/services/Http';
import { convertToMomentOrNull, validText } from '~/utils/helper';

import { useSxStyles } from './useStyles';

type OwnProps = {
  employeeId: number;
  dialogOpen: boolean;
  editDrAllCustomFields: Record<string, CustomField[]>;
  addDrEmptyCustomFields: CustomField[];
  handleClose: () => void;
  fetchDecisionRegistryData: () => void;
  decision: Decision;
  updateCustomFields: (customData: Object) => void;
  register?: (rules: HTMLInputElement | null) => Ref<HTMLInputElement>;
  getValues: UseFormGetValues<AnswerPreProcess>;
  setValue: (code: string, date: string | string[]) => void;
  errors: Record<string, { message: string }>;
  handleSubmit: UseFormHandleSubmit<AnswerPreProcess>;
};

type Props = OwnProps;

export const DecisionRegistryForm: React.FC<Props> = ({
  employeeId,
  dialogOpen,
  handleClose,
  fetchDecisionRegistryData,
  decision,
  editDrAllCustomFields,
  addDrEmptyCustomFields,
  updateCustomFields,
  register,
  getValues,
  setValue,
  errors,
  handleSubmit,
}) => {
  const inEditMode = !!decision;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const { firstName, lastName } = useLoggedInEmployeeProfile();

  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [decisionInfo, setDecisionInfo] = useState('');
  const [reminderDate, setReminderDate] = useState<string>('');
  const [showValidationError, setShowValidationError] = useState({
    type: false,
    title: false,
  });

  const customFieldRules = {
    [CustomFieldTypes.STRING]: {
      validate: {
        maxLength: (value: string) =>
          value.length < 80 || t('laErrorMaxTextLen1') + ' 80',
      },
    },
    [CustomFieldTypes.TEXT]: {
      validate: {
        maxLength: (value: string) =>
          value.length < 3000 || t('laErrorMaxTextLen1') + ' 3000',
      },
    },
    [CustomFieldTypes.NUMBER]: {
      validate: {
        pattern: (value: string) =>
          /^-?\d*,?\d*$/.test(value) || t('laCheckNumber'),
      },
    },
  };

  useEffect(() => {
    if (decision) {
      setType(decision.type);
      setTitle(decision.title);
      setDecisionInfo(decision.info || '');
      setSelectedFile(decision.file as File);
      setReminderDate(decision.reminderDate || moment().format('YYYY-MM-DD'));
    }
  }, [decision]);

  const clearState = () => {
    setType('');
    setTitle('');
    setDecisionInfo('');
    setSelectedFile(null);
    setReminderDate(moment().format('YYYY-MM-DD'));
    setShowValidationError({ type: false, title: false });
  };

  const handleSaveData = useMemo(() => {
    return handleSubmit((customFieldValues) => {
      const formData = new FormData();
      if (!validText(type)) {
        setShowValidationError({
          type: true,
          title: showValidationError.title,
        });
        return;
      }
      if (!validText(title)) {
        setShowValidationError({ type: showValidationError.type, title: true });
        return;
      }

      formData.append('type', type);
      formData.append('title', title);
      formData.append('maker', `${firstName} ${lastName}`);
      formData.append('info', decisionInfo);
      if (reminderDate !== null) {
        formData.append('reminderDate', reminderDate as string);
      }
      formData.append('isSystemGenerated', 'false');
      formData.append('file', selectedFile as File);

      const addURL = '/form/employee/' + employeeId + '/decisionregistry/add';
      let updateURL = '';
      let customForm = {};
      if (decision) {
        updateURL =
          '/form/employee/' +
          employeeId +
          '/decisionregistry/' +
          decision.id +
          '/update';
        customForm = formatFieldsValue(
          customFieldValues as Record<string, string | boolean>,
          editDrAllCustomFields[decision.id]
            ? editDrAllCustomFields[decision.id]
            : addDrEmptyCustomFields,
        );
      } else {
        customForm = formatFieldsValue(
          customFieldValues as Record<string, string | boolean>,
          addDrEmptyCustomFields,
        );
      }

      if (inEditMode) {
        fetchFromOldApi(updateURL, {
          method: 'PUT',
          body: formData,
        }).then(() => {
          fetchFromOldApi(`/json/customdata/DECISION_REGISTRY/${decision.id}`, {
            method: 'PUT',
            body: JSON.stringify(customForm) as BodyInit,
            headers: { 'Content-Type': 'application/json' },
          }).then(() => {
            fetchDecisionRegistryData();
            handleClose();
            clearState();
          });
        });
      } else {
        fetchFromOldApi(addURL, {
          method: 'POST',
          body: formData,
        })
          .then((r) => r.json())
          .then((data) => {
            const dataResponse = data as Response & { id: string };
            fetchFromOldApi(
              `/json/customdata/DECISION_REGISTRY/${dataResponse.id}`,
              {
                method: 'PUT',
                body: JSON.stringify(customForm) as BodyInit,
                headers: { 'Content-Type': 'application/json' },
              },
            ).then(() => {
              fetchDecisionRegistryData();
              handleClose();
              clearState();
            });
          });
      }
    });
  }, [
    type,
    title,
    decisionInfo,
    reminderDate,
    selectedFile,
    addDrEmptyCustomFields,
    decision,
    editDrAllCustomFields,
    employeeId,
    fetchDecisionRegistryData,
    firstName,
    handleClose,
    handleSubmit,
    inEditMode,
    lastName,
    showValidationError.title,
    showValidationError.type,
  ]);

  const handleReminderChange = (date: Moment | null) => {
    setReminderDate(!!date ? date?.format('YYYY-MM-DD') : '');
  };

  const handleFileUpload = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;
    setSelectedFile(target.files ? target.files[0] : null);
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        handleClose();
        clearState();
      }}
    >
      <DialogTitle id="form-dialog-title">
        {t('laAddToDecisionRegistry')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('laManualDecisionRegistry')}</DialogContentText>

        <TextField
          required
          autoFocus
          margin="dense"
          id="decisionType"
          label="Type"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setShowValidationError({
              type: false,
              title: showValidationError.title,
            });
          }}
          disabled={inEditMode && decision.isSystemGenerated}
          error={showValidationError.type}
          helperText={showValidationError.type ? t('laThisRequired') : ' '}
          fullWidth
        />
        <TextField
          required
          margin="dense"
          id="decisionTitle"
          label="Title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setShowValidationError({
              title: false,
              type: showValidationError.type,
            });
          }}
          disabled={inEditMode && decision.isSystemGenerated}
          error={showValidationError.title}
          helperText={showValidationError.title ? t('laThisRequired') : ' '}
          fullWidth
        />
        <TextField
          margin="dense"
          id="decisionInfo"
          label="Info"
          value={decisionInfo}
          onChange={(e) => setDecisionInfo(e.target.value)}
          fullWidth
        />
        <form>
          {decision && decision.id && editDrAllCustomFields[decision.id]
            ? editDrAllCustomFields[decision.id].map(
                (customField: CustomField) => {
                  return (
                    <Grid key={customField.code} item={true}>
                      <LegacyCustomField
                        fieldDetails={customField}
                        isSaving={inEditMode && decision.isSystemGenerated}
                        idEditMode={true}
                        formValues={getValues()}
                        setValue={setValue}
                        updateFormValues={updateCustomFields}
                        validationRules={customFieldRules}
                        errors={errors}
                        register={register}
                        classes={{} as ClassNameMap<CustomFieldStyleKeys>}
                      />
                    </Grid>
                  );
                },
              )
            : addDrEmptyCustomFields.map((customField: CustomField) => {
                return (
                  <Grid key={customField.code} item={true}>
                    <LegacyCustomField
                      fieldDetails={customField}
                      isSaving={inEditMode && decision.isSystemGenerated}
                      idEditMode={true}
                      formValues={getValues()}
                      setValue={setValue}
                      errors={errors}
                      validationRules={customFieldRules}
                      updateFormValues={updateCustomFields}
                      register={register}
                      classes={{} as ClassNameMap<CustomFieldStyleKeys>}
                    />
                  </Grid>
                );
              })}
        </form>
        <br />
        <br />
        <div>
          <span>{t('laAttachment')}: </span>
          <Box
            component="input"
            accept="*"
            sx={sxStyles.inputFileUpload}
            id="raised-button-file"
            multiple
            type="file"
            onChange={(e: ChangeEvent) => handleFileUpload(e)}
          />
          <label htmlFor="raised-button-file">
            <Button variant="contained" component="span">
              {selectedFile || (decision && decision.attachmentName)
                ? t('laChangeFile')
                : t('laChooseFile')}
            </Button>
          </label>
          <Box component="span" sx={sxStyles.fileButton}>
            {selectedFile
              ? selectedFile.name
              : decision && decision.attachmentName
              ? decision && decision.attachmentName
              : t('laNoFile')}
          </Box>
        </div>
        <br />
        <DatePicker
          label={t('laReminder')}
          value={convertToMomentOrNull(reminderDate)}
          minDate={moment()}
          onChange={handleReminderChange}
          slotProps={{
            textField: {
              variant: 'standard',
            },
          }}
        />
        <Button
          variant="contained"
          component="span"
          onClick={() => setReminderDate('')}
          sx={sxStyles.reminderButton}
        >
          {t('laNoReminder')}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleClose();
            clearState();
          }}
          color="primary"
        >
          {t('laCancel')}
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            handleSaveData();
          }}
          color="primary"
        >
          {t('laSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
